import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  getReviewSession,
  httpStatus,
  upsertReviewSessionAnnotation,
  ANNOTATION_TAGS,
  type LatencyDistribution,
  type ReviewSessionStats,
  type ReviewTimelineItem,
  type UpsertAnnotationBody,
} from '@/api/reviewStudio';
import { Timeline } from '@/components/ReviewStudio/Timeline';
import { SessionLanes } from '@/components/ReviewStudio/SessionLanes';
import { formatMs } from '@/components/ReviewStudio/JsonDrawer';
import type { VideoPlayerRef } from '@/components/VideoPlayerView/useVideoPlayer';
import { AnnotationForm } from '@/components/ReviewStudio/AnnotationForm';
import { ForkCard } from '@/components/ReviewStudio/ForkCard';
import { ConfigPanel } from '@/components/ReviewStudio/ConfigPanel';
import { VideoInspector } from '@/components/ReviewStudio/VideoInspector';
import { StudioGate } from '@/components/ReviewStudio/StudioGate';
import { StudioRecording } from '@/components/ReviewStudio/StudioVideo';
import { DemoVideoPanel } from '@/components/ReviewStudio/DemoVideoPanel';

export const Route = createFileRoute('/_auth/reviews/$roomId')({
  component: ReviewStudioDetail,
});

function ReviewStudioDetail() {
  return (
    <StudioGate>
      <ReviewStudioDetailBody />
    </StudioGate>
  );
}

function ReviewStudioDetailBody() {
  const { roomId } = Route.useParams();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<ReviewTimelineItem | null>(null);
  const [pane, setPane] = useState<'timeline' | 'video'>('timeline');
  const playerRef = useRef<VideoPlayerRef>(null);
  const [playheadTs, setPlayheadTs] = useState<number | null>(null);
  // Manual nudge (s) when the recording anchor is approximate (older rooms) or egress lagged.
  const [syncOffsetS, setSyncOffsetS] = useState(0);

  const query = useQuery({
    queryKey: ['review-session', roomId],
    queryFn: () => getReviewSession(roomId),
  });

  const recordingAnchor =
    query.data?.recording_started_at_ts ?? query.data?.session_started_at_ts ?? null;
  const canSeek = !!query.data?.recording_url && recordingAnchor != null;

  const seekToTs = (ts: number) => {
    if (recordingAnchor == null) return;
    playerRef.current?.seekTo(Math.max(0, ts - recordingAnchor + syncOffsetS));
  };

  useEffect(() => {
    if (!canSeek || recordingAnchor == null) return;
    const timer = window.setInterval(() => {
      const current = playerRef.current?.getCurrentTime();
      if (current == null) return;
      const ts = recordingAnchor + current - syncOffsetS;
      setPlayheadTs((prev) => (prev != null && Math.abs(prev - ts) < 0.05 ? prev : ts));
    }, 200);
    return () => window.clearInterval(timer);
  }, [canSeek, recordingAnchor, syncOffsetS]);

  // Latest conversation row the video has reached.
  const activeId = useMemo(() => {
    if (playheadTs == null || !query.data) return null;
    let active: string | null = null;
    for (const item of query.data.timeline) {
      if (item.ts == null || item.kind === 'metrics' || item.kind === 'state') continue;
      if (item.ts > playheadTs) break;
      active = item.id;
    }
    return active;
  }, [playheadTs, query.data]);

  const existing = useMemo(() => {
    const annotations = query.data?.annotations || [];
    if (!selected) {
      return annotations.find((item) => item.target === 'session');
    }
    return annotations.find((item) => item.target_id === selected.id);
  }, [query.data?.annotations, selected]);

  // The script step the model was on when the selected line was spoken: what it was told to do.
  const directive = useMemo(() => {
    if (!selected?.ts || !query.data) return null;
    let last: ReviewTimelineItem | null = null;
    for (const item of query.data.timeline) {
      if (item.ts == null || item.ts > selected.ts) continue;
      if (item.event_type === 'script_directive' && (!last || (last.ts ?? 0) <= item.ts)) last = item;
    }
    return last;
  }, [selected, query.data]);

  const mutation = useMutation({
    mutationFn: (payload: UpsertAnnotationBody) => upsertReviewSessionAnnotation(roomId, payload),
    onSuccess: () => {
      toast.success('Saved');
      queryClient.invalidateQueries({ queryKey: ['review-session', roomId] });
      queryClient.invalidateQueries({ queryKey: ['review-sessions'] });
    },
    onError: () => toast.error('Could not save note'),
  });

  if (query.isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-40 animate-pulse rounded-full bg-neutral-100" />
        <div className="h-40 animate-pulse rounded-3xl bg-neutral-100" />
      </div>
    );
  }

  if (httpStatus(query.error) === 403) {
    return (
      <div className="rounded-3xl bg-white px-6 py-16 text-center ring-1 ring-black/5">
        <p className="text-sm font-medium text-neutral-900">Staff only</p>
        <Link to="/dashboard" className="mt-3 inline-block text-sm text-neutral-500 underline">
          Back to dashboard
        </Link>
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <div className="rounded-3xl bg-white px-6 py-16 text-center ring-1 ring-black/5">
        <p className="text-sm font-medium text-neutral-900">Session not found</p>
        <Link to="/reviews" className="mt-3 inline-block text-sm text-neutral-500 underline">
          Back to studio
        </Link>
      </div>
    );
  }

  const session = query.data;
  const hasVideo = session.video_stories.length > 0;

  return (
    <div className="space-y-6">
      <Link
        to="/reviews"
        className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-900"
      >
        <ChevronLeft className="h-4 w-4" />
        Studio
      </Link>

      <div className="overflow-hidden rounded-3xl bg-white ring-1 ring-black/5">
        <div className="space-y-2 px-5 py-4">
          <h1 className="text-2xl font-semibold tracking-tight text-neutral-900">
            {session.session_name || 'Untitled session'}
          </h1>
          <p className="text-sm text-neutral-500">
            {session.company_name ? `${session.company_name} · ` : ''}
            {session.candidate_name || 'Unknown learner'}
            {session.candidate_email ? ` · ${session.candidate_email}` : ''}
          </p>
          <div className="flex flex-wrap gap-1.5 pt-1">
            <span className="rounded-full bg-neutral-100 px-2 py-0.5 font-mono text-[11px] text-neutral-600">
              {session.room_id}
            </span>
            {session.room_public_id && (
              <span className="rounded-full bg-neutral-100 px-2 py-0.5 font-mono text-[11px] text-neutral-600">
                {session.room_public_id}
              </span>
            )}
            {session.session_public_id && (
              <span className="rounded-full bg-neutral-100 px-2 py-0.5 font-mono text-[11px] text-neutral-600">
                {session.session_public_id}
              </span>
            )}
            {session.session_type && (
              <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] text-neutral-600">
                {session.session_type}
              </span>
            )}
            <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] text-neutral-600">
              {session.status || 'unknown'}
            </span>
            {session.has_debug_events ? (
              <span className="rounded-full bg-[#AAE73D] px-2 py-0.5 text-[11px] text-neutral-900">
                debug timeline
              </span>
            ) : (
              <span className="rounded-full bg-neutral-50 px-2 py-0.5 text-[11px] text-neutral-400">
                partial data
              </span>
            )}
          </div>
        </div>
      </div>

      <StatsStrip stats={session.stats} />

      {session.session_template_id && (
        <DemoVideoPanel sessionTemplateId={session.session_template_id} />
      )}

      {session.missing_fields.length > 0 && (
        <div className="rounded-2xl bg-neutral-50 px-4 py-3 text-xs leading-relaxed text-neutral-500">
          Not stored yet: {session.missing_fields.join(' · ')}
        </div>
      )}

      {hasVideo && (
        <div className="flex gap-1.5">
          {(['timeline', 'video'] as const).map((id) => (
            <button
              key={id}
              type="button"
              onClick={() => setPane(id)}
              className={`h-9 rounded-full px-4 text-[13px] font-medium ${
                pane === id
                  ? 'bg-[#AAE73D] text-neutral-900'
                  : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
              }`}
            >
              {id === 'timeline' ? 'Timeline' : 'Video story'}
            </button>
          ))}
        </div>
      )}

      {pane === 'video' && hasVideo ? (
        <VideoInspector
          roomId={roomId}
          stories={session.video_stories}
          onRetryStored={() => queryClient.invalidateQueries({ queryKey: ['review-session', roomId] })}
        />
      ) : (
        <div className="flex flex-col gap-6 lg:flex-row">
          <div className="min-w-0 flex-1 space-y-4">
            {session.session_started_at_ts != null && (
              <SessionLanes
                items={session.timeline}
                sessionStartTs={session.session_started_at_ts}
                selectedId={selected?.id || null}
                playheadTs={playheadTs}
                annotations={session.annotations}
                onSelect={(item) => {
                  setSelected(item);
                  if (canSeek && item.ts != null) seekToTs(item.ts);
                }}
                onSeekTs={canSeek ? seekToTs : undefined}
              />
            )}
            <div className="flex items-baseline justify-between">
              <h2 className="text-sm font-semibold text-neutral-900">Timeline</h2>
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="text-xs text-neutral-500 hover:text-neutral-900"
              >
                Annotate whole session
              </button>
            </div>
            <Timeline
              items={session.timeline}
              selectedId={selected?.id || null}
              activeId={activeId}
              annotations={session.annotations}
              hasDebugEvents={session.has_debug_events}
              canSeek={canSeek}
              onSelect={setSelected}
              onSeek={(item) => item.ts != null && seekToTs(item.ts)}
            />
          </div>

          <aside className="w-full shrink-0 lg:w-[420px]">
            <div className="sticky top-20 max-h-[calc(100vh-6rem)] space-y-4 overflow-y-auto pb-4">
              {session.recording_url && (
                <div className="space-y-1.5">
                  <StudioRecording roomId={session.room_id} playerRef={playerRef} />
                  {canSeek && (
                    <div className="flex items-center gap-2 px-1 text-[11px] text-neutral-500">
                      <span>
                        {session.recording_started_at_ts ? 'Synced to egress start' : 'Approximate sync'}
                      </span>
                      <span className="ml-auto">Nudge</span>
                      {[-0.5, 0.5].map((delta) => (
                        <button
                          key={delta}
                          type="button"
                          onClick={() => setSyncOffsetS((prev) => +(prev + delta).toFixed(1))}
                          className="h-6 rounded-full bg-neutral-100 px-2 hover:bg-neutral-200"
                        >
                          {delta > 0 ? '+' : ''}
                          {delta}s
                        </button>
                      ))}
                      <span className="w-10 text-right font-mono">{syncOffsetS}s</span>
                    </div>
                  )}
                </div>
              )}
              {selected?.kind === 'transcript' && (
                <ForkCard key={selected.id} roomId={session.room_id} item={selected} />
              )}
              <div className="rounded-3xl bg-white p-4 ring-1 ring-black/5">
                <AnnotationForm
                  key={selected?.id || 'session'}
                  selectedItem={selected}
                  directive={directive}
                  existing={existing}
                  saving={mutation.isPending}
                  onSave={(payload) => mutation.mutate(payload)}
                />
              </div>
              {session.annotations.length > 0 && (
                <div className="rounded-3xl bg-white p-4 ring-1 ring-black/5">
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-neutral-400">
                    Saved notes
                  </p>
                  <div className="space-y-2">
                    {session.annotations.map((item) => (
                      <div key={item.id} className="rounded-2xl bg-neutral-50 px-3 py-2">
                        <p className="text-[11px] uppercase tracking-wide text-neutral-400">
                          {item.target} · T{item.taste} Q{item.quality} G{item.hallucination}
                          {item.is_bug ? ' · bug' : ''}
                        </p>
                        {item.tags?.length > 0 && (
                          <p className="mt-0.5 text-[11px] text-neutral-600">
                            {item.tags
                              .map((tag) => ANNOTATION_TAGS.find((t) => t.id === tag)?.label ?? tag)
                              .join(' · ')}
                          </p>
                        )}
                        {item.notes && (
                          <p className="mt-1 text-xs leading-relaxed text-neutral-700">{item.notes}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="rounded-3xl bg-white p-4 ring-1 ring-black/5">
                <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-neutral-400">
                  Agent config
                </p>
                <ConfigPanel config={session.session_config} />
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

function StatCell({ label, dist, warnAt }: { label: string; dist: LatencyDistribution; warnAt: number }) {
  if (!dist.count) return null;
  const hot = (dist.p95 ?? 0) >= warnAt;
  return (
    <div className="rounded-2xl bg-white px-3 py-2 ring-1 ring-black/5">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-neutral-400">{label}</p>
      <p className={`font-mono text-[13px] ${hot ? 'text-red-600' : 'text-neutral-900'}`}>
        {formatMs(dist.p50)} <span className="text-neutral-400">p50</span> · {formatMs(dist.p95)}{' '}
        <span className="text-neutral-400">p95</span>
      </p>
      <p className="font-mono text-[10px] text-neutral-400">
        max {formatMs(dist.max)} · n={dist.count}
      </p>
    </div>
  );
}

function CountCell({ label, value, bad }: { label: string; value: number | string; bad?: boolean }) {
  return (
    <div className="rounded-2xl bg-white px-3 py-2 ring-1 ring-black/5">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-neutral-400">{label}</p>
      <p className={`font-mono text-[13px] ${bad ? 'text-red-600' : 'text-neutral-900'}`}>{value}</p>
    </div>
  );
}

function StatsStrip({ stats }: { stats?: ReviewSessionStats }) {
  if (!stats) return null;
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-8">
      <StatCell label="Response (e2e)" dist={stats.e2e_ms} warnAt={2000} />
      <StatCell label="LLM ttft" dist={stats.ttft_ms} warnAt={1200} />
      <StatCell label="TTS ttfb" dist={stats.ttfb_ms} warnAt={800} />
      <StatCell label="End of turn" dist={stats.eot_ms} warnAt={1000} />
      <StatCell label="UI observer" dist={stats.observer_ms} warnAt={4000} />
      <CountCell label="Turns" value={`${stats.learner_turns} / ${stats.tutor_turns}`} />
      <CountCell
        label="Answers"
        value={stats.answers_total ? `${stats.answers_right}✓ ${stats.answers_wrong}✗ / ${stats.answers_total}` : '—'}
      />
      <CountCell
        label="Cut off · missed · errors"
        value={`${stats.interrupted_turns} · ${stats.false_interruptions + stats.transcription_timeouts} · ${stats.errors}`}
        bad={stats.errors > 0 || stats.transcription_timeouts > 0}
      />
    </div>
  );
}
